import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { AuthService } from "../../shared/services/auth.service";
import * as AuthActions from './auth.actions';
import { catchError, map, of, switchMap, tap } from "rxjs";
import { Router } from "@angular/router";
import { MessageService } from 'primeng/api';
import { CookieService } from '../../shared/services/cookie.service';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  private jwtHelper = inject(JwtHelperService);

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(action =>
        this.authService.login(action.username, action.password).pipe(
          map(payload => AuthActions.loginSuccess({ token: payload.access_token })),
          catchError(error => of(AuthActions.loginError({ error })))
        )
      )
    );
  });

  handleLoginSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ token }) => {
        this.cookieService.setCookie('jwt', token, 1);
      }),
      map(({ token }) => {
        const decodedToken = this.jwtHelper.decodeToken(token);
        const user = {
          id: decodedToken.id,
          username: decodedToken.login,
          login: decodedToken.login,
          name: decodedToken.name,
          email: decodedToken.email,
          phone_number: decodedToken.phone_number,
          role: decodedToken.role,
          isAuthenticated: true,
        };

        return AuthActions.loadUserSuccess({ user });
      })
    );
  });

  loginRedirect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Login successful'
        });
        this.router.navigate(['/dashboard']);
      })
    );
  }, { dispatch: false });

  handleLoginError$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginError),
      tap(({ error }) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Login failed: ' + (error.message.detail || 'Invalid credentials')
        });
      })
    );
  }, { dispatch: false });

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        this.cookieService.deleteCookie('jwt');
        this.messageService.add({
          severity: 'info',
          summary: 'Info',
          detail: 'Successfully logged out'
        });
        this.router.navigate(['/login']);
      }),
      map(() => AuthActions.logoutSuccess())
    );
  });
}
